import React, { SyntheticEvent, useState } from 'react';
import { Alert, Button, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { PulseLoader } from 'react-spinners';
import { GameModel } from '../types/GamesModel';
import { describeEvaluationError } from './evaluationError';
import { useEvaluationOverlay } from './EvaluationOverlayProvider';
import {
  EvaluationStrength,
  STRENGTH_OPTIONS,
  readStrength,
  writeStrength,
} from './evaluationStrength';

// The overlay's evaluate action, a split button: the main half runs at the selected strength, the
// caret picks a strength and runs. EvaluationOverlayProvider runs the request and freezes the overlay
// with the mini-game loader meanwhile.
export default function GamesOverlayEvaluate(props: { game: GameModel }): React.ReactNode {
  const { game } = props;
  const { evaluating, error, evaluate } = useEvaluationOverlay();
  // From localStorage: finishing an evaluation remounts this, so component state would forget the
  // choice.
  const [strength, setStrength] = useState<EvaluationStrength>(readStrength);
  const loading = evaluating;
  const evaluated = game.LastEvaluation != null;
  const label = evaluated ? 'Re-evaluate' : 'Evaluate';

  const run = (level: EvaluationStrength) => {
    setStrength(level);
    writeStrength(level);
    evaluate(game.Id, level);
  };

  const onClick = (e: SyntheticEvent) => {
    e.preventDefault();
    run(strength);
  };

  return (
    <div className={`cm-overlay-eval${evaluated ? ' is-evaluated' : ''}`}>
      {error ? (
        <Alert variant="danger" className="mb-2">
          <div>{describeEvaluationError(error).message}</div>
          <div className="small text-muted">{describeEvaluationError(error).hint}</div>
        </Alert>
      ) : null}
      <Dropdown as="div" className="cm-evaluate-split">
        <Button className="cm-evaluate-btn" disabled={loading} onClick={onClick}>
          {loading ? (
            <PulseLoader color="#ffffff" size={8} />
          ) : (
            <>
              <FontAwesomeIcon icon={faGaugeHigh} className="me-2" />
              {label}
              <span className="cm-evaluate-level">{strength}</span>
            </>
          )}
        </Button>
        <Dropdown.Toggle
          split
          className="cm-evaluate-caret"
          disabled={loading}
          aria-label="Choose evaluation strength"
        />
        <Dropdown.Menu align="end" className="cm-evaluate-menu">
          <Dropdown.Header>How hard should the engine think?</Dropdown.Header>
          {STRENGTH_OPTIONS.map((o) => (
            <Dropdown.Item
              key={o.key}
              active={o.key === strength}
              onClick={() => run(o.key)}
              className="cm-evaluate-item"
            >
              <span className="cm-evaluate-item-head">
                {o.key === strength ? (
                  <FontAwesomeIcon icon={faCheck} className="cm-evaluate-item-check" />
                ) : (
                  <span className="cm-evaluate-item-check" />
                )}
                {o.label}
              </span>
              <span className="cm-evaluate-item-hint">{o.hint}</span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
